import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Menu,
  Radio,
  RadioGroup,
  Slider,
} from "@mui/material"
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material"
import { MouseEvent, useState } from "react"
import { useLocalStorage } from "usehooks-ts"

export type StackedBarChartOptions = {
  percentileUpperValue?: number | string
  sliderUpperValue: number
  percentileLowerValue?: number | string
  sliderLowerValue: number
  isMean: boolean
  isStacked?: boolean
  showStDev?: boolean
  showAsPercentage?: boolean
}

export const STACKED_BAR_MULTI_NODE_CHART_OPTIONS_KEY = "stacked-bar-multi-node-chart-options"

export const defaultOptions: StackedBarChartOptions = {
  percentileUpperValue: 95,
  sliderUpperValue: 70,
  percentileLowerValue: 5,
  sliderLowerValue: 30,
  isMean: false,
  isStacked: true,
  showAsPercentage: false,
}

const ChartOptionsDropdown = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null)
  const [options, setOptions] = useLocalStorage<StackedBarChartOptions>(
    STACKED_BAR_MULTI_NODE_CHART_OPTIONS_KEY,
    defaultOptions,
  )

  const handleClick = (event: MouseEvent<HTMLDivElement>) => setAnchorEl(event.currentTarget)
  const handleClose = () => setAnchorEl(null)

  return (
    <>
      <Grid
        container
        onClick={handleClick}
        alignItems="center"
        sx={{
          cursor: "pointer",
          p: 1,
          "&:hover": { backgroundColor: "rgba(0, 0, 0, .04)" },
        }}
      >
        <Grid item> {anchorEl ? <KeyboardArrowUp /> : <KeyboardArrowDown />}</Grid>
        <Grid item>Chart options</Grid>
      </Grid>

      <Menu
        id="chart-options"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "chart-options",
          sx: { width: "250px" },
        }}
      >
        <FormControlLabel
          sx={{ ml: 1 }}
          label="Show mean aggregate"
          control={
            <Checkbox
              checked={options.isMean}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setOptions((prevState) => {
                  return {
                    ...prevState,
                    isMean: event.target.checked,
                  }
                })
              }}
            />
          }
        />
        <FormControlLabel
          sx={{ ml: 1 }}
          label="Show stacked bars"
          control={
            <Checkbox
              checked={options.isStacked}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setOptions((prevState) => {
                  return {
                    ...prevState,
                    isStacked: event.target.checked,
                  }
                })
              }}
            />
          }
        />
        <FormControlLabel
          sx={{ ml: 1 }}
          label="Show as percentages"
          control={
            <Checkbox
              checked={options.showAsPercentage}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setOptions((prevState) => {
                  return {
                    ...prevState,
                    showAsPercentage: event.target.checked,
                  }
                })
              }}
            />
          }
        />
        <FormControl>
          <FormLabel sx={{ ml: 2 }}>Lower</FormLabel>
          <RadioGroup
            value={options.percentileLowerValue}
            onChange={({ target }) =>
              setOptions((prevState) => ({
                ...prevState,
                percentileLowerValue: target.value,
              }))
            }
            sx={{ ml: 2 }}
          >
            <FormControlLabel
              value="0"
              control={<Radio size="small" disabled={options.isMean} />}
              label="None"
            />
            <FormControlLabel
              value="5"
              control={<Radio size="small" disabled={options.isMean} />}
              label="5th Percentile"
            />
            <FormControlLabel
              value="10"
              control={<Radio size="small" disabled={options.isMean} />}
              label="10th Percentile"
            />
            <FormControlLabel
              value="25"
              control={<Radio size="small" disabled={options.isMean} />}
              label="25th Percentile"
            />
            <FormControlLabel
              value="custom"
              control={<Radio size="small" disabled={options.isMean} />}
              label="Other"
            />
          </RadioGroup>
          <Slider
            sx={{ ml: 2, width: 220 }}
            min={1}
            max={49}
            value={options.sliderLowerValue}
            onChange={(_event, value) =>
              setOptions((prevState) => ({
                ...prevState,
                sliderLowerValue: +value,
              }))
            }
            disabled={options.percentileLowerValue !== "custom" || options.isMean}
            valueLabelDisplay="auto"
          />
        </FormControl>
        <FormControl>
          <FormLabel sx={{ ml: 2 }}>Upper</FormLabel>
          <RadioGroup
            value={options.percentileUpperValue}
            onChange={({ target }) =>
              setOptions((prevState) => ({
                ...prevState,
                percentileUpperValue: target.value,
              }))
            }
            sx={{ ml: 2 }}
          >
            <FormControlLabel
              value="0"
              control={<Radio size="small" disabled={options.isMean} />}
              label="None"
            />
            <FormControlLabel
              value="75"
              control={<Radio size="small" disabled={options.isMean} />}
              label="75th Percentile"
            />
            <FormControlLabel
              value="90"
              control={<Radio size="small" disabled={options.isMean} />}
              label="90th Percentile"
            />
            <FormControlLabel
              value="95"
              control={<Radio size="small" disabled={options.isMean} />}
              label="95th Percentile"
            />
            <FormControlLabel
              value="custom"
              control={<Radio size="small" disabled={options.isMean} />}
              label="Other"
            />
          </RadioGroup>
          <Slider
            sx={{ ml: 2, width: 220 }}
            min={51}
            max={99}
            value={options.sliderUpperValue}
            onChange={(_event, value) =>
              setOptions((prevState) => ({
                ...prevState,
                sliderUpperValue: +value,
              }))
            }
            disabled={options.percentileUpperValue !== "custom" || options.isMean}
            valueLabelDisplay="auto"
          />
        </FormControl>
      </Menu>
    </>
  )
}

export default ChartOptionsDropdown
